// Super Master > Completar dados (pedido 2026-10-01): a importação de
// veículos deixou campos vazios (linha, modelo etc.). Escolhe a empresa,
// sobe a planilha completa, confere as colunas e o sistema acha cada
// veículo pelo IMEI e SÓ preenche o que está vazio — nunca muda nome,
// nunca sobrescreve um valor que já existe, nunca cria veículo. Primeiro
// "Analisar" (mostra o que vai mudar), depois "Aplicar".
import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useRef, useState } from "react";
import { loadXlsx } from "@/lib/xlsx-lazy";
import { toast } from "sonner";
import { CheckCircle2, FileSpreadsheet, Loader2, Search, Upload, Wand2 } from "lucide-react";
import { MasterShell } from "@/components/master-shell";
import { MasterSmsTabs } from "@/components/master-sms-tabs";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useCompanies } from "@/hooks/queries/platform";
import { platformPost, PlatformApiError } from "@/lib/api/gpsdrive-platform-client";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/master/vehicle-fill")({
  component: () => (
    <MasterShell>
      <MasterSmsTabs />
      <VehicleFillPage />
    </MasterShell>
  ),
  head: () => ({ meta: [{ title: "Completar dados — Super Master — GPS Drive" }] }),
});

type FieldId =
  | "imei"
  | "simNumber"
  | "phone"
  | "simCarrier"
  | "simIccid"
  | "simApn"
  | "trackerSupplier"
  | "trackerBrand"
  | "trackerModel"
  | "protocol"
  | "trackerType"
  | "plate"
  | "brand"
  | "model"
  | "year"
  | "color"
  | "chassis"
  | "fipeValue"
  | "trackerPrice"
  | "cameraImei"
  | "installCompany"
  | "installLocation"
  | "installedAt"
  | "installTechnician"
  | "installWarranty"
  | "installNotes"
  | "notes";
const IGNORE = "";

const FIELDS: { id: FieldId; label: string; synonyms: string[] }[] = [
  { id: "imei", label: "IMEI", synonyms: ["imei", "uniqueid", "serial", "identificador", "imeidorastreador"] },
  { id: "simNumber", label: "Número da linha", synonyms: ["linha", "numerodalinha", "telefone", "chip", "numerosim", "sim", "numero", "fone", "msisdn", "numerochip"] },
  { id: "phone", label: "Telefone do chip (SMS)", synonyms: ["telefonedochip", "telefonesms"] },
  { id: "simCarrier", label: "Operadora", synonyms: ["operadora", "carrier"] },
  { id: "simIccid", label: "ICCID", synonyms: ["iccid"] },
  { id: "simApn", label: "APN", synonyms: ["apn"] },
  { id: "trackerSupplier", label: "Fornecedor do chip", synonyms: ["fornecedor", "fornecedordochip", "supplier"] },
  { id: "trackerBrand", label: "Marca do rastreador", synonyms: ["marcadorastreador", "marcarastreador", "fabricante"] },
  { id: "trackerModel", label: "Modelo do rastreador", synonyms: ["modelodorastreador", "modelorastreador", "rastreador", "equipamento", "modeloequipamento", "modelodispositivo", "modelododispositivo"] },
  { id: "protocol", label: "Protocolo", synonyms: ["protocolo", "protocol"] },
  { id: "trackerType", label: "Tipo (rastreador/tag/câmera)", synonyms: ["tipo", "tipodedispositivo", "tipodispositivo", "type"] },
  { id: "plate", label: "Placa", synonyms: ["placa", "plate"] },
  { id: "brand", label: "Marca do veículo", synonyms: ["marca", "marcadoveiculo", "montadora"] },
  { id: "model", label: "Modelo do veículo", synonyms: ["modelo", "modelodoveiculo"] },
  { id: "year", label: "Ano", synonyms: ["ano", "anomodelo", "year"] },
  { id: "color", label: "Cor", synonyms: ["cor", "color"] },
  { id: "chassis", label: "Chassi", synonyms: ["chassi", "chassis", "vin"] },
  { id: "fipeValue", label: "Valor FIPE", synonyms: ["valorfipe", "fipe", "valordafipe"] },
  { id: "trackerPrice", label: "Valor do equipamento", synonyms: ["valordoequipamento", "valorequipamento", "valordorastreador"] },
  { id: "cameraImei", label: "IMEI da câmera", synonyms: ["imeidacamera", "imeicamera"] },
  { id: "installCompany", label: "Empresa da instalação", synonyms: ["empresadainstalacao", "empresainstaladora", "instaladora"] },
  { id: "installLocation", label: "Local da instalação", synonyms: ["localdainstalacao", "localinstalacao"] },
  { id: "installedAt", label: "Data de instalação", synonyms: ["datadeinstalacao", "datainstalacao", "instaladoem"] },
  { id: "installTechnician", label: "Instalador", synonyms: ["instalador", "tecnico", "tecnicoinstalador"] },
  { id: "installWarranty", label: "Garantia", synonyms: ["garantia"] },
  { id: "installNotes", label: "Observações da instalação", synonyms: ["observacoesdainstalacao", "obsinstalacao"] },
  { id: "notes", label: "Observações", synonyms: ["observacoes", "observacao", "obs", "notas"] },
];
const FIELD_LABEL = Object.fromEntries(FIELDS.map((f) => [f.id, f.label])) as Record<string, string>;

const norm = (s: string) =>
  s
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

// Mesmo tratamento da importação: IMEI grande não pode virar "8,6E+14" e
// data vira AAAA-MM-DD.
function cellToText(v: unknown): string {
  if (v === null || v === undefined) return "";
  if (v instanceof Date) {
    if (Number.isNaN(v.getTime())) return "";
    const pad = (n: number) => String(n).padStart(2, "0");
    return `${v.getFullYear()}-${pad(v.getMonth() + 1)}-${pad(v.getDate())}`;
  }
  if (typeof v === "number") return Number.isInteger(v) ? v.toLocaleString("fullwide", { useGrouping: false }) : String(v);
  return String(v).trim();
}

// Cabeçalho igual a um sinônimo; depois cabeçalho que CONTÉM "imei"/"placa"
// etc.; por fim, sem coluna de IMEI pelo nome, a coluna cujos valores são
// quase todos números de 14–20 dígitos vira o IMEI.
function autoMap(headers: string[], rows: string[][]): Record<number, FieldId | typeof IGNORE> {
  const used = new Set<FieldId>();
  const map: Record<number, FieldId | typeof IGNORE> = {};
  headers.forEach((h, i) => {
    const n = norm(h);
    const f = FIELDS.find((d) => !used.has(d.id) && d.synonyms.includes(n));
    map[i] = f ? f.id : IGNORE;
    if (f) used.add(f.id);
  });
  const loose: [FieldId, RegExp][] = [
    ["imei", /imei|uniqueid|serial/],
    ["plate", /placa/],
    ["simNumber", /linha|chip|msisdn|telefone|celular/],
    ["simCarrier", /operadora/],
    ["simIccid", /iccid/],
  ];
  for (const [field, re] of loose) {
    if (used.has(field)) continue;
    const i = headers.findIndex((h, idx) => !map[idx] && re.test(norm(h)) && !/camera/.test(norm(h)));
    if (i >= 0) {
      map[i] = field;
      used.add(field);
    }
  }
  if (!used.has("imei")) {
    const sample = rows.slice(0, 200);
    let best = -1;
    let bestScore = 0;
    headers.forEach((_, i) => {
      if (map[i]) return;
      const filled = sample.filter((r) => r[i]);
      if (!filled.length) return;
      const score = filled.filter((r) => /^\d{14,20}$/.test(r[i].replace(/\s+/g, ""))).length / filled.length;
      if (score > bestScore) {
        bestScore = score;
        best = i;
      }
    });
    if (best >= 0 && bestScore >= 0.6) map[best] = "imei";
  }
  return map;
}

interface FillResult {
  line: number;
  imei: string;
  status: "fill" | "nothing" | "not_found" | "other_company" | "invalid";
  deviceId?: number;
  deviceName?: string;
  filled: Record<string, string>;
  kept: string[];
  notes: string[];
  error?: string;
}

const BATCH = 300;
const STATUS_LABEL: Record<FillResult["status"], { label: string; cls: string }> = {
  fill: { label: "Vai completar", cls: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300" },
  nothing: { label: "Já completo", cls: "bg-muted text-muted-foreground" },
  not_found: { label: "IMEI não encontrado", cls: "bg-amber-500/15 text-amber-800 dark:text-amber-300" },
  other_company: { label: "Não é desta empresa", cls: "bg-rose-500/15 text-rose-700 dark:text-rose-300" },
  invalid: { label: "Erro", cls: "bg-rose-500/15 text-rose-700 dark:text-rose-300" },
};

function VehicleFillPage() {
  const { data: companies = [] } = useCompanies();
  const [companyQ, setCompanyQ] = useState("");
  const [companyId, setCompanyId] = useState("");
  const fileRef = useRef<HTMLInputElement>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [headers, setHeaders] = useState<string[]>([]);
  const [rows, setRows] = useState<string[][]>([]);
  const [headerOffset, setHeaderOffset] = useState(0);
  const [mapping, setMapping] = useState<Record<number, FieldId | typeof IGNORE>>({});
  const [busy, setBusy] = useState<null | "analyze" | "apply">(null);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [results, setResults] = useState<FillResult[] | null>(null);
  const [applied, setApplied] = useState(false);
  const [filter, setFilter] = useState<FillResult["status"] | "">("fill");

  const filteredCompanies = useMemo(() => {
    const s = companyQ.trim().toLowerCase();
    return s ? companies.filter((c) => c.name.toLowerCase().includes(s)) : companies;
  }, [companies, companyQ]);

  const imeiCol = Object.entries(mapping).find(([, f]) => f === "imei")?.[0];
  const plateCol = Object.entries(mapping).find(([, f]) => f === "plate")?.[0];
  const mappedCount = Object.values(mapping).filter((f) => f && f !== "imei").length;
  // O que ainda falta pra liberar "Analisar" — sempre mostrado na tela, o
  // botão nunca fica cinza sem dizer por quê.
  const missing: string[] = [];
  if (!companyId) missing.push("Escolha a empresa (passo 1).");
  if (!fileName) missing.push("Escolha a planilha (passo 2).");
  else if (!rows.length) missing.push("A planilha não tem linhas com dados abaixo do cabeçalho.");
  else {
    if (imeiCol === undefined && plateCol === undefined) missing.push("Marque qual coluna é o IMEI (ou a Placa).");
    if (!mappedCount) missing.push("Marque pelo menos uma coluna com informação pra completar (ex.: Número da linha).");
  }

  const reset = () => {
    setResults(null);
    setApplied(false);
  };

  const handleFile = async (file: File) => {
    try {
      const XLSX = await loadXlsx();
      const wb = XLSX.read(await file.arrayBuffer(), { type: "array", cellDates: true });
      const name = wb.SheetNames.find((n) => XLSX.utils.sheet_to_json(wb.Sheets[n], { header: 1 }).length > 1);
      if (!name) {
        toast.error("Planilha vazia");
        return;
      }
      const aoa = XLSX.utils.sheet_to_json<unknown[]>(wb.Sheets[name], { header: 1, defval: "", raw: true });
      // Cabeçalho = 1ª linha (entre as 10 primeiras) que tem algum nome de
      // coluna conhecido — planilha com título em cima não quebra.
      const known = new Set(FIELDS.flatMap((f) => f.synonyms));
      let headerIdx = aoa.slice(0, 10).findIndex((r) => (r ?? []).some((c) => known.has(norm(cellToText(c)))));
      if (headerIdx < 0) headerIdx = Math.max(0, aoa.findIndex((r) => (r ?? []).filter((c) => cellToText(c)).length >= 2));
      const hdrs = (aoa[headerIdx] ?? []).map(cellToText);
      const data = aoa
        .slice(headerIdx + 1)
        .map((r) => hdrs.map((_, i) => cellToText(r[i])))
        .filter((r) => r.some((c) => c !== ""));
      setFileName(file.name);
      setHeaders(hdrs);
      setHeaderOffset(headerIdx);
      setRows(data);
      setMapping(autoMap(hdrs, data));
      reset();
    } catch {
      toast.error("Não foi possível ler este arquivo", { description: "Use .xlsx, .xls ou .csv." });
    } finally {
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const buildPayload = () =>
    rows.map((cells, i) => {
      const values: Record<string, string> = {};
      for (const [col, field] of Object.entries(mapping)) {
        if (!field || field === "imei") continue;
        const v = (cells[Number(col)] ?? "").trim();
        if (v) values[field] = v;
      }
      return { line: i + headerOffset + 2, imei: imeiCol === undefined ? "" : (cells[Number(imeiCol)] ?? "").replace(/\s+/g, ""), values };
    });

  const run = async (dryRun: boolean) => {
    if (missing.length) return;
    const payload = buildPayload();
    // Aplicar: só as linhas que a análise disse que completam algo.
    const toSend = dryRun ? payload : payload.filter((p) => results?.some((r) => r.line === p.line && r.status === "fill"));
    setBusy(dryRun ? "analyze" : "apply");
    setProgress({ done: 0, total: toSend.length });
    const out: FillResult[] = [];
    try {
      for (let i = 0; i < toSend.length; i += BATCH) {
        const chunk = toSend.slice(i, i + BATCH);
        const res = await platformPost<FillResult[]>(`/api/v1/platform-migration/companies/${companyId}/vehicle-fill`, {
          dryRun,
          rows: chunk,
        });
        out.push(...res);
        setProgress({ done: Math.min(i + BATCH, toSend.length), total: toSend.length });
      }
      if (dryRun) {
        setResults(out);
        setApplied(false);
        setFilter(out.some((r) => r.status === "fill") ? "fill" : "");
      } else {
        const byLine = new Map(out.map((r) => [r.line, r]));
        setResults((prev) => (prev ?? []).map((r) => byLine.get(r.line) ?? r));
        setApplied(true);
        const ok = out.filter((r) => r.status === "fill").length;
        const errors = out.filter((r) => r.status === "invalid").length;
        toast.success(`${ok} veículo(s) completados`, errors ? { description: `${errors} com erro — veja a lista.` } : undefined);
      }
    } catch (err) {
      toast.error(dryRun ? "Não foi possível analisar" : "Parou no meio da aplicação", {
        description: err instanceof PlatformApiError || err instanceof Error ? err.message : undefined,
      });
      if (!dryRun && out.length) {
        const byLine = new Map(out.map((r) => [r.line, r]));
        setResults((prev) => (prev ?? []).map((r) => byLine.get(r.line) ?? r));
      }
    } finally {
      setBusy(null);
    }
  };

  const counts = useMemo(() => {
    const c: Record<string, number> = {};
    for (const r of results ?? []) c[r.status] = (c[r.status] ?? 0) + 1;
    return c;
  }, [results]);
  const fieldsToFill = useMemo(() => {
    const c: Record<string, number> = {};
    for (const r of results ?? []) for (const f of Object.keys(r.filled)) c[f] = (c[f] ?? 0) + 1;
    return Object.entries(c).sort((a, b) => b[1] - a[1]);
  }, [results]);
  const visible = (results ?? []).filter((r) => !filter || r.status === filter);

  return (
    <div className="mx-auto max-w-6xl space-y-5 p-6">
      <div>
        <h1 className="flex items-center gap-2 text-2xl font-bold">
          <Wand2 className="h-6 w-6" /> Completar dados dos veículos
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Acha cada veículo pelo IMEI e preenche <b>só os campos vazios</b> (linha, operadora, modelo, placa etc.). Nome do veículo e
          qualquer valor já preenchido nunca são alterados.
        </p>
      </div>

      {/* 1. Empresa */}
      <Card>
        <CardContent className="space-y-3 pt-6">
          <div className="text-sm font-semibold">1. Empresa</div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <input
                value={companyQ}
                onChange={(e) => setCompanyQ(e.target.value)}
                placeholder="Buscar empresa…"
                className="h-9 w-56 rounded-md border bg-background pl-8 pr-2 text-sm"
              />
            </div>
            <select
              value={companyId}
              onChange={(e) => {
                setCompanyId(e.target.value);
                reset();
              }}
              className="h-9 min-w-[260px] rounded-md border bg-background px-2 text-sm"
            >
              <option value="">Escolha a empresa</option>
              {filteredCompanies.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
        </CardContent>
      </Card>

      {/* 2. Planilha */}
      <Card>
        <CardContent className="space-y-3 pt-6">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="text-sm font-semibold">2. Planilha</div>
            <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={(e) => e.target.files?.[0] && handleFile(e.target.files[0])} />
            <Button variant="outline" onClick={() => fileRef.current?.click()}>
              <Upload className="h-4 w-4" /> {fileName ? "Trocar planilha" : "Escolher planilha"}
            </Button>
          </div>
          {fileName && (
            <>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <FileSpreadsheet className="h-4 w-4" /> {fileName} — {rows.length} linha(s)
              </div>
              <div className="text-xs text-muted-foreground">
                Confira de qual informação é cada coluna. Colunas em “Ignorar” não são usadas (ex.: nome do veículo).
              </div>
              <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                {headers.map((h, i) => (
                  <label key={i} className={cn("flex items-center gap-2 rounded-md border p-2 text-sm", mapping[i] && "border-primary/40 bg-primary/5")}>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate font-medium">{h || `Coluna ${i + 1}`}</span>
                      <span className="block truncate text-[11px] text-muted-foreground">ex.: {rows[0]?.[i] || "—"}</span>
                    </span>
                    <select
                      value={mapping[i] ?? IGNORE}
                      onChange={(e) => {
                        const v = e.target.value as FieldId | typeof IGNORE;
                        setMapping((m) => {
                          const next = { ...m };
                          // Um campo só pode vir de uma coluna.
                          if (v) for (const k of Object.keys(next)) if (next[Number(k)] === v) next[Number(k)] = IGNORE;
                          next[i] = v;
                          return next;
                        });
                        reset();
                      }}
                      className="h-8 w-44 rounded-md border bg-background px-1.5 text-xs"
                    >
                      <option value={IGNORE}>Ignorar</option>
                      {FIELDS.map((f) => (
                        <option key={f.id} value={f.id}>
                          {f.label}
                        </option>
                      ))}
                    </select>
                  </label>
                ))}
              </div>
              {imeiCol === undefined && plateCol === undefined && (
                <div className="text-sm font-medium text-rose-600">
                  Não achei a coluna do IMEI pelo nome — escolha “IMEI” na caixinha da coluna certa (ou “Placa”).
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* 3. Analisar / aplicar */}
      <Card>
        <CardContent className="space-y-4 pt-6">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="text-sm font-semibold">3. Conferir e aplicar</div>
            <div className="flex gap-2">
              <Button variant="outline" disabled={missing.length > 0 || !!busy} onClick={() => run(true)}>
                {busy === "analyze" && <Loader2 className="h-4 w-4 animate-spin" />} Analisar
              </Button>
              <Button disabled={!results || !counts.fill || applied || !!busy} onClick={() => run(false)}>
                {busy === "apply" && <Loader2 className="h-4 w-4 animate-spin" />}
                {applied ? (
                  <>
                    <CheckCircle2 className="h-4 w-4" /> Aplicado
                  </>
                ) : (
                  `Completar ${counts.fill ?? 0} veículo(s)`
                )}
              </Button>
            </div>
          </div>
          {busy && (
            <div className="space-y-1">
              <div className="h-2 overflow-hidden rounded-full bg-muted">
                <div className="h-full bg-primary transition-all" style={{ width: `${progress.total ? (progress.done / progress.total) * 100 : 0}%` }} />
              </div>
              <div className="text-xs text-muted-foreground">
                {busy === "analyze" ? "Analisando" : "Gravando"} {progress.done} de {progress.total}…
              </div>
            </div>
          )}
          {!busy && missing.length > 0 && (
            <div className="rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-sm text-amber-900 dark:text-amber-200">
              <div className="font-medium">Falta pra liberar:</div>
              <ul className="ml-4 list-disc">
                {missing.map((m) => (
                  <li key={m}>{m}</li>
                ))}
              </ul>
            </div>
          )}
          {!results && !busy && (
            <p className="text-sm text-muted-foreground">
              “Analisar” só mostra o que vai ser preenchido — nada é gravado até clicar em “Completar”. Cada veículo é achado pelo IMEI
              (e, se o IMEI não existir, pela placa). Só campos vazios são preenchidos; o que já está preenchido nunca é alterado nem
              apagado.
            </p>
          )}
          {results && !applied && !busy && !counts.fill && (
            <p className="text-sm font-medium text-amber-700 dark:text-amber-300">
              Nenhum veículo tem campo vazio pra completar com essa planilha — veja os filtros abaixo (já completo / não encontrado /
              outra empresa).
            </p>
          )}
          {results && (
            <>
              <div className="flex flex-wrap gap-2">
                {(["fill", "nothing", "not_found", "other_company", "invalid"] as const).map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => setFilter((f) => (f === s ? "" : s))}
                    className={cn("rounded-full px-3 py-1 text-xs font-medium", STATUS_LABEL[s].cls, filter === s && "ring-2 ring-primary")}
                  >
                    {applied && s === "fill" ? "Completados" : STATUS_LABEL[s].label}: {counts[s] ?? 0}
                  </button>
                ))}
              </div>
              {fieldsToFill.length > 0 && (
                <div className="text-xs text-muted-foreground">
                  Campos {applied ? "preenchidos" : "que serão preenchidos"}:{" "}
                  {fieldsToFill.map(([f, n]) => `${FIELD_LABEL[f] ?? (f === "phone" ? "Telefone do chip (SMS)" : f)} (${n})`).join(" · ")}
                </div>
              )}
              <div className="max-h-[520px] overflow-auto rounded-md border">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-background">
                    <tr className="border-b text-left text-xs uppercase text-muted-foreground">
                      <th className="px-2 py-2">Linha</th>
                      <th className="px-2 py-2">IMEI / veículo</th>
                      <th className="px-2 py-2">Situação</th>
                      <th className="px-2 py-2">Vai preencher</th>
                      <th className="px-2 py-2">Mantido (já tinha)</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {visible.slice(0, 1000).map((r) => (
                      <tr key={r.line} className="align-top">
                        <td className="px-2 py-1.5 text-xs text-muted-foreground">{r.line}</td>
                        <td className="px-2 py-1.5">
                          <div className="font-mono text-xs">{r.imei || "—"}</div>
                          {r.deviceName && <div className="text-xs text-muted-foreground">{r.deviceName}</div>}
                        </td>
                        <td className="px-2 py-1.5">
                          <span className={cn("rounded-full px-2 py-0.5 text-[11px] font-medium", STATUS_LABEL[r.status].cls)}>
                            {applied && r.status === "fill" ? "Completado" : STATUS_LABEL[r.status].label}
                          </span>
                          {r.error && <div className="mt-0.5 text-[11px] text-rose-600">{r.error}</div>}
                          {r.notes.map((n, i) => (
                            <div key={i} className="mt-0.5 text-[11px] text-amber-700 dark:text-amber-300">
                              {n}
                            </div>
                          ))}
                        </td>
                        <td className="px-2 py-1.5 text-xs">
                          {Object.entries(r.filled).map(([f, v]) => (
                            <div key={f}>
                              <span className="text-muted-foreground">{FIELD_LABEL[f] ?? (f === "phone" ? "Telefone do chip (SMS)" : f)}:</span> {v}
                            </div>
                          ))}
                        </td>
                        <td className="px-2 py-1.5 text-xs text-muted-foreground">
                          {r.kept.map((f) => FIELD_LABEL[f] ?? f).join(", ")}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {visible.length > 1000 && <div className="p-2 text-center text-xs text-muted-foreground">Mostrando 1000 de {visible.length}.</div>}
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
